import { useEffect, useMemo, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { PeriodSelector } from "@/components/workflow/common/PeriodSelector";
import { DEFAULT_FREIGHT_ENTRY } from "@/contexts/wizard/useScope3State";
import { calculateFreightEmissions } from "@/service/emissions";

const modeLabels = {
  road_freight_hgv: "Road HGV",
  road_freight_van: "Road Van",
  air_freight: "Air Freight",
  sea_freight: "Sea Freight",
  rail_freight: "Rail Freight",
  courier_small_parcel: "Courier / Small Parcel",
} as const;

interface Props {
  isOpen: boolean;
}

export default function FreightTransportFormSection({ isOpen }: Props) {
  const {
    scope3Data,
    assessment,
    currentFreightEntry,
    setCurrentFreightEntry,
    addFreightEntry,
    deleteFreightEntry,
    updateFreightEntry,
    forceSave,
    isLocked,
    isReadOnly,
  } = useWizard();
  const { toast } = useToast();
  const disabled = isLocked || isReadOnly;
  const pendingSaveRef = useRef(false);
  const [activeDirection, setActiveDirection] = useState<"upstream" | "downstream">("upstream");
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      forceSave();
    }
  }, [scope3Data.freightEntries, forceSave]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentFreightEntry(DEFAULT_FREIGHT_ENTRY);
      setEditingEntryId(null);
      // Do not reset activeDirection — it's independent
    }
  }, [isOpen]);

  const upstreamTotal = useMemo(() => {
    return scope3Data.freightEntries
      .filter((entry) => entry.direction === "upstream")
      .reduce((sum, entry) => sum + (entry.tco2e || 0), 0);
  }, [scope3Data.freightEntries]);

  const downstreamTotal = useMemo(() => {
    return scope3Data.freightEntries
      .filter((entry) => entry.direction === "downstream")
      .reduce((sum, entry) => sum + (entry.tco2e || 0), 0);
  }, [scope3Data.freightEntries]);

  const totalTco2e = upstreamTotal + downstreamTotal;
  const completion = scope3Data.freightEntries.length > 0 ? 100 : 0;

  const isCourier = currentFreightEntry.mode === "courier_small_parcel";

  const handleAddEntry = async () => {
    const result = await addFreightEntry();
    if (!result.ok) {
      toast({ title: result.error || "Unable to add freight entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Freight entry added",
      description: result.tco2e
        ? `${result.tco2e.toFixed(3)} tCO2e`
        : "Calculating...",
    });
  };

  const handleEditEntry = (entry: (typeof scope3Data.freightEntries)[number]) => {
    setCurrentFreightEntry({
      direction: entry.direction,
      mode: entry.mode,
      weightTonnes: entry.weightTonnes,
      distanceKm: entry.distanceKm,
      parcelCount: entry.parcelCount,
      unit: entry.unit,
      period: entry.period,
      // tonneKm is derived — NOT loaded
    });
    setActiveDirection(entry.direction); // sync direction tab
    setEditingEntryId(entry.id);
  };

  const handleCancelEdit = () => {
    setCurrentFreightEntry({ ...DEFAULT_FREIGHT_ENTRY, direction: activeDirection });
    setEditingEntryId(null);
  };

  const handleUpdateEntry = async () => {
    const id = editingEntryId!;
    const { unit, weightTonnes = 0, distanceKm = 0, parcelCount } = currentFreightEntry;

    // Recompute derived fields with branching on unit
    let tonneKm: number | undefined;
    let finalParcelCount: number | undefined;
    if (unit === "parcel") {
      finalParcelCount = parcelCount;
      tonneKm = undefined;
    } else {
      tonneKm = weightTonnes * distanceKm;
      finalParcelCount = undefined;
    }

    updateFreightEntry(id, {
      ...currentFreightEntry,
      tonneKm,
      parcelCount: finalParcelCount,
      calculating: true,
    });

    try {
      const entryForCalc = {
        ...currentFreightEntry,
        id,
        tonneKm,
        parcelCount: finalParcelCount,
      };
      const tco2e = await calculateFreightEmissions(entryForCalc);
      updateFreightEntry(id, { tco2e, calculating: false });
      pendingSaveRef.current = true;
      setCurrentFreightEntry(DEFAULT_FREIGHT_ENTRY);
      setEditingEntryId(null);
      toast({ title: "Entry updated", description: tco2e != null ? `${tco2e.toFixed(3)} tCO₂e` : undefined });
    } catch {
      toast({ title: "Failed to update entry", variant: "destructive" });
      updateFreightEntry(id, { calculating: false });
    }
  };

  return (
    <div className="space-y-6">
      {disabled && <LockedBanner />}

      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setActiveDirection("upstream");
            setCurrentFreightEntry((prev) => ({ ...prev, direction: "upstream" }));
          }}
          disabled={disabled}
          className={
            activeDirection === "upstream"
              ? "rounded-2xl border-primary bg-primary text-white hover:bg-primary/80 hover:text-white"
              : "rounded-2xl border-slate-200 bg-white text-primary hover:bg-primary/5"
          }
        >
          {"Upstream Freight (goods received)"}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setActiveDirection("downstream");
            setCurrentFreightEntry((prev) => ({ ...prev, direction: "downstream" }));
          }}
          disabled={disabled}
          className={
            activeDirection === "downstream"
              ? "rounded-2xl border-primary bg-primary text-white hover:bg-primary/80 hover:text-white"
              : "rounded-2xl border-slate-200 bg-white text-primary hover:bg-primary/5"
          }
        >
          {"Downstream Freight (goods shipped)"}
        </Button>
      </div>

      <div className="form-surface-card">
        <div className="flex flex-col gap-6 p-6">
          <h4 className="text-sm font-bold leading-5 text-gray-800">{"Add Freight Entry"}</h4>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="form-label">{"Transport mode"}</Label>
              <Select
                value={currentFreightEntry.mode}
                onValueChange={(value) =>
                  setCurrentFreightEntry((prev) => ({
                    ...prev,
                    mode: value as typeof currentFreightEntry.mode,
                    unit: value === "courier_small_parcel" ? "parcel" : "tonne_km",
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger className="form-select">
                  <SelectValue placeholder={"Select mode"} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(modeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

          </div>

          {!isCourier ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="form-label">{"Weight (tonnes)"}</Label>
                <Input
                  className="form-input"
                  type="number"
                  min={0}
                  step="0.01"
                  value={currentFreightEntry.weightTonnes ?? ""}
                  onChange={(e) =>
                    setCurrentFreightEntry((prev) => ({
                      ...prev,
                      weightTonnes: e.target.value === "" ? undefined : Number(e.target.value),
                      unit: "tonne_km",
                    }))
                  }
                  placeholder={"e.g. 12"}
                  disabled={disabled}
                />
              </div>
              <div className="space-y-2">
                <Label className="form-label">{"Distance (km)"}</Label>
                <Input
                  className="form-input"
                  type="number"
                  min={0}
                  step="0.1"
                  value={currentFreightEntry.distanceKm ?? ""}
                  onChange={(e) =>
                    setCurrentFreightEntry((prev) => ({
                      ...prev,
                      distanceKm: e.target.value === "" ? undefined : Number(e.target.value),
                      unit: "tonne_km",
                    }))
                  }
                  placeholder={"e.g. 350"}
                  disabled={disabled}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="form-label">{"Parcel count"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="1"
                value={currentFreightEntry.parcelCount ?? ""}
                onChange={(e) =>
                  setCurrentFreightEntry((prev) => ({
                    ...prev,
                    parcelCount: e.target.value === "" ? undefined : Number(e.target.value),
                    unit: "parcel",
                  }))
                }
                placeholder={"e.g. 120"}
                disabled={disabled}
              />
            </div>
          )}

          <PeriodSelector
            periodStart={assessment!.periodStart}
            periodEnd={assessment!.periodEnd}
            value={currentFreightEntry.period ?? null}
            onChange={(value) => setCurrentFreightEntry((prev) => ({ ...prev, period: value }))}
            disabled={disabled}
          />

          <div className="flex gap-2">
            {editingEntryId !== null ? (
              <>
                <Button onClick={handleUpdateEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </>
            ) : (
              <Button onClick={handleAddEntry} disabled={disabled} className="form-add-button">
                <Plus className="mr-2 h-3 w-3" /> {"Add Entry"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="entries-header">
          <h4 className="entries-title">{"Entries"}</h4>
          <span className="entries-count">
            {`${scope3Data.freightEntries.length} entries`}
          </span>
        </div>

        {scope3Data.freightEntries.length === 0 ? (
          <p className="entries-empty">{"No freight entries yet."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="table-row-base">
                <TableHead className="table-head-base">{"Direction"}</TableHead>
                <TableHead className="table-head-base">{"Mode"}</TableHead>
                <TableHead className="table-head-base text-center">{"Quantity"}</TableHead>
                <TableHead className="table-head-base text-center">{"Period"}</TableHead>
                <TableHead className="table-head-base text-center">{"tCO2e"}</TableHead>
                <TableHead className="table-head-base text-right">{"Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scope3Data.freightEntries.map((entry) => (
                <TableRow
                  key={entry.id}
                  className={`table-row-base${editingEntryId === entry.id ? " outline outline-2 outline-primary/30" : ""}`}
                >
                  <TableCell className="table-cell-base capitalize">
                    {entry.direction === "upstream"
                      ? "Upstream"
                      : "Downstream"}
                  </TableCell>
                  <TableCell className="table-cell-base-strong">
                    {modeLabels[entry.mode] ? modeLabels[entry.mode] : entry.mode}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.unit === "parcel"
                      ? `${entry.parcelCount ?? 0} parcels`
                      : `${(entry.tonneKm ?? 0).toFixed(2)} tonne-km`}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.calculating ? "Calculating..." : (entry.tco2e ?? 0).toFixed(3)}
                  </TableCell>
                  <TableCell className="table-cell-base-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditEntry(entry)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      disabled={disabled || (editingEntryId !== null && editingEntryId !== entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        deleteFreightEntry(entry.id);
                        pendingSaveRef.current = true;
                      }}
                      className="delete-button"
                      disabled={disabled || editingEntryId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="total-card">
          <p className="total-label">{"Upstream Total"}</p>
          <p className="total-value">
            {`${upstreamTotal.toFixed(3)} tCO2e`}
          </p>
        </div>
        <div className="total-card">
          <p className="total-label">{"Downstream Total"}</p>
          <p className="total-value">
            {`${downstreamTotal.toFixed(3)} tCO2e`}
          </p>
        </div>
        <div className="total-card">
          <p className="total-label">{"Combined Total"}</p>
          <p className="total-value">
            {`${totalTco2e.toFixed(3)} tCO2e`}
          </p>
        </div>
      </div>

      <Progress value={completion} className="form-progress" />
      <p className="text-xs leading-4 text-slate-500">{`Completion: ${completion}%`}</p>
    </div>
  );
}
